/**
 * Ein Punkt im Bild verfolgen.
 *
 * Sascha hat sich das als S13 gewünscht: „Ein Gesicht verfolgen und den
 * Weichzeichner mitziehen — oder eine Bauchbinde daran hängen.“ Der zweite
 * Teil geht damit heute; der erste, sobald es einen Weichzeichner gibt, der
 * nur einen Bereich trifft.
 *
 * Das Verfahren ist das einfachste, das taugt: ein Ausschnitt aus dem ersten
 * Bild (die *Vorlage*) wird im nächsten Bild in einem Fenster um die letzte
 * Stelle gesucht, und zwar dort, wo die Summe der Helligkeitsunterschiede am
 * kleinsten ist. Kein neuronales Netz, keine Bibliothek, nichts, was das
 * Telefon herunterladen müsste — und für einen Kopf, der sich in einer
 * ruhigen Einstellung bewegt, reicht es.
 *
 * Zwei Dinge entscheiden darüber, ob das Ergebnis brauchbar ist, und beide
 * stehen hier drin statt in einer Fußnote:
 *
 *   - **Eine flache Vorlage taugt nicht.** Ein Ausschnitt aus einer weißen
 *     Wand passt überall gleich gut; das Ergebnis wäre Zufall. Deshalb wird
 *     der Kontrast gemessen, und darunter sagt das Verfahren nein, statt zu
 *     raten.
 *   - **Ein Fund kann schlecht sein.** Verlässt das Motiv das Bild oder
 *     verdeckt es etwas, bleibt der beste Fund im Fenster trotzdem
 *     irgendeiner. Deshalb kommt zu jedem Fund eine Güte, und wer verfolgt,
 *     hört auf, wenn sie fällt — ein Verfolger, der abgerissen ist und weiter
 *     Punkte schreibt, ist schlimmer als einer, der stehen bleibt.
 *
 * Gerechnet wird auf Graustufen: Helligkeit trägt die Struktur, und ein
 * Drittel der Daten ist ein Drittel der Arbeit.
 */
/** Aus RGBA (wie `getImageData` es liefert) Graustufen machen. */
export function zuGrau(rgba, breite, hoehe) {
    const daten = new Uint8Array(breite * hoehe);
    for (let i = 0; i < daten.length; i++) {
        const p = i * 4;
        // Die üblichen Gewichte: Grün trägt die meiste Helligkeit für das Auge.
        daten[i] = (((rgba[p] ?? 0) * 77 + (rgba[p + 1] ?? 0) * 150 + (rgba[p + 2] ?? 0) * 29) >> 8) & 0xff;
    }
    return { daten, breite, hoehe };
}
/**
 * Ein Ausschnitt. `x`/`y` ist die linke obere Ecke; was über den Rand ragt,
 * wird abgeschnitten -- ein Ausschnitt am Bildrand ist kleiner, nicht falsch.
 */
export function bildAusschnitt(bild, x, y, breite, hoehe) {
    // Beschnitten und nicht verschoben: ein verschobener Ausschnitt saehe aus
    // wie der gewuenschte und zeigte etwas anderes -- und damit wanderte der
    // verfolgte Punkt, ohne dass es jemand merkt.
    const links = Math.round(x);
    const oben = Math.round(y);
    const x0 = Math.max(0, links);
    const y0 = Math.max(0, oben);
    const b = Math.max(0, Math.min(bild.breite, links + Math.round(breite)) - x0);
    const h = Math.max(0, Math.min(bild.hoehe, oben + Math.round(hoehe)) - y0);
    const daten = new Uint8Array(b * h);
    for (let zeile = 0; zeile < h; zeile++) {
        const von = (y0 + zeile) * bild.breite + x0;
        daten.set(bild.daten.subarray(von, von + b), zeile * b);
    }
    return { daten, breite: b, hoehe: h };
}
/**
 * Wie viel Struktur eine Vorlage hat, 0..1 (die mittlere Abweichung vom
 * eigenen Mittelwert, auf 128 bezogen). Alles unter `MINDESTKONTRAST` ist zu
 * flach, um darin etwas wiederzufinden.
 */
export function kontrast(vorlage) {
    const n = vorlage.daten.length;
    if (n === 0)
        return 0;
    let summe = 0;
    for (let i = 0; i < n; i++)
        summe += vorlage.daten[i] ?? 0;
    const mittel = summe / n;
    let ab = 0;
    for (let i = 0; i < n; i++)
        ab += Math.abs((vorlage.daten[i] ?? 0) - mittel);
    return Math.min(1, ab / n / 128);
}
export const MINDESTKONTRAST = 0.04;
/** Darunter gilt ein Fund als verloren und die Verfolgung hört auf. */
export const MINDESTGUETE = 0.86;
/**
 * Die Vorlage um (`x`,`y`) herum suchen, höchstens `radius` Punkte weit.
 *
 * Zurück kommt die beste Stelle und ihre Güte. Wird gar nichts geprüft (leere
 * Vorlage, Fenster außerhalb), ist die Güte 0.
 */
export function suche(bild, vorlage, x, y, radius) {
    const n = vorlage.daten.length;
    if (n === 0 || vorlage.breite > bild.breite || vorlage.hoehe > bild.hoehe) {
        return { x, y, guete: 0 };
    }
    const maxX = bild.breite - vorlage.breite;
    const maxY = bild.hoehe - vorlage.hoehe;
    let bestX = Math.max(0, Math.min(maxX, Math.round(x)));
    let bestY = Math.max(0, Math.min(maxY, Math.round(y)));
    let bestSumme = Infinity;
    for (let dy = -radius; dy <= radius; dy++) {
        const py = Math.round(y) + dy;
        if (py < 0 || py > maxY)
            continue;
        for (let dx = -radius; dx <= radius; dx++) {
            const px = Math.round(x) + dx;
            if (px < 0 || px > maxX)
                continue;
            let summe = 0;
            for (let zeile = 0; zeile < vorlage.hoehe; zeile++) {
                const vonBild = (py + zeile) * bild.breite + px;
                const vonVorlage = zeile * vorlage.breite;
                for (let spalte = 0; spalte < vorlage.breite; spalte++) {
                    summe += Math.abs((bild.daten[vonBild + spalte] ?? 0) - (vorlage.daten[vonVorlage + spalte] ?? 0));
                }
                // Früh aufhören: diese Stelle kann nicht mehr gewinnen.
                if (summe >= bestSumme)
                    break;
            }
            if (summe < bestSumme) {
                bestSumme = summe;
                bestX = px;
                bestY = py;
            }
        }
    }
    if (!Number.isFinite(bestSumme))
        return { x, y, guete: 0 };
    return { x: bestX, y: bestY, guete: Math.max(0, 1 - bestSumme / n / 255) };
}
/**
 * Ein Schritt der Verfolgung: die Vorlage im neuen Bild wiederfinden.
 *
 * `mitteX`/`mitteY` ist die letzte bekannte *Mitte*; gerechnet wird intern
 * mit der linken oberen Ecke, weil eine Vorlage dort liegt.
 */
export function verfolge(bild, vorlage, mitteX, mitteY, radius) {
    if (kontrast(vorlage) < MINDESTKONTRAST) {
        return { mitteX, mitteY, guete: 0, abbruch: 'flach' };
    }
    const treffer = suche(bild, vorlage, mitteX - vorlage.breite / 2, mitteY - vorlage.hoehe / 2, radius);
    const neu = {
        mitteX: treffer.x + vorlage.breite / 2,
        mitteY: treffer.y + vorlage.hoehe / 2,
        guete: treffer.guete,
    };
    return treffer.guete < MINDESTGUETE ? { ...neu, abbruch: 'verloren' } : neu;
}
/** Was dem Menschen gesagt wird, wenn die Verfolgung aufhört. */
export function abbruchSatz(grund) {
    if (grund === 'flach')
        return 'An dieser Stelle ist zu wenig zu erkennen — setz die Ebene auf etwas mit Kontrast.';
    return 'Die Spur ist abgerissen. Bis hierher stehen die Punkte; ab hier von Hand weiter.';
}
//# sourceMappingURL=verfolgen.js.map